import { CheckIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { Content, CONTENT_TYPES } from "@/types";

interface ChatMessageEditProps {
  content: Content[];
  isUser: boolean;
  onSave: (newContent: Content[]) => void;
  onCancel: () => void;
}

/**
 * Extracts the combined text from text-type content blocks.
 */
const getEditableText = (content: Content[]): string =>
  content
    .filter(c => c.type === CONTENT_TYPES.TEXT)
    .map(c => ("text" in c ? c.text : ""))
    .join("\n");

/**
 * Builds a new content array by replacing existing text blocks with edited text.
 * Non-text blocks (images, media, tool calls, etc.) are preserved as-is.
 *
 * If the original content had multiple text blocks they are collapsed into one
 * because the user edited them as a single string. This is the safest approach
 * to avoid creating empty orphan blocks.
 */
const buildUpdatedContent = (originalContent: Content[], newText: string): Content[] => {
  const nonTextBlocks = originalContent.filter(c => c.type !== CONTENT_TYPES.TEXT);
  const trimmed = newText.trim();

  if (trimmed.length === 0) {
    return nonTextBlocks;
  }

  const newTextBlock: Content = { type: CONTENT_TYPES.TEXT, text: trimmed } as Content;
  const firstTextIndex = originalContent.findIndex(c => c.type === CONTENT_TYPES.TEXT);

  if (firstTextIndex === -1) {
    return [...nonTextBlocks, newTextBlock];
  }

  const result: Content[] = [];
  let textInserted = false;

  for (const block of originalContent) {
    if (block.type === CONTENT_TYPES.TEXT) {
      if (!textInserted) {
        result.push(newTextBlock);
        textInserted = true;
      }
    } else {
      result.push(block);
    }
  }

  return result;
};

export const ChatMessageEdit = ({ content, isUser, onSave, onCancel }: ChatMessageEditProps) => {
  const [editText, setEditText] = useState(() => getEditableText(content));
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, []);

  const handleSave = useCallback(() => {
    const newContent = buildUpdatedContent(content, editText);
    if (newContent.length === 0) {
      return;
    }
    onSave(newContent);
  }, [content, editText, onSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        handleSave();
      }
    },
    [onCancel, handleSave],
  );

  const hasChanged = editText !== getEditableText(content);

  return (
    <div className="flex w-full flex-col gap-1">
      <Textarea
        ref={textareaRef}
        value={editText}
        onChange={e => setEditText(e.target.value)}
        onKeyDown={handleKeyDown}
        autoSize
        className={cn(
          "min-h-[2rem] w-full resize-none rounded-lg border-0 bg-transparent px-0 py-0",
          "text-sm leading-relaxed",
          "focus:outline-none focus:ring-0",
          isUser
            ? "text-gray-900 placeholder:text-blue-300"
            : "text-gray-900 placeholder:text-gray-400",
        )}
        placeholder="Enter message text…"
      />
      <div className="flex items-center gap-1">
        <span className="mr-auto select-none text-[10px] text-gray-400">⌘↵ save · esc cancel</span>
        <button
          type="button"
          onClick={onCancel}
          className={cn(
            "flex h-6 w-6 items-center justify-center rounded-full",
            "text-gray-400 transition-colors hover:bg-black/5 hover:text-gray-600",
          )}
          aria-label="Cancel editing"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanged}
          className={cn(
            "flex h-6 w-6 items-center justify-center rounded-full transition-colors",
            hasChanged
              ? "text-blue-500 hover:bg-blue-50 hover:text-blue-600"
              : "cursor-not-allowed text-gray-300",
          )}
          aria-label="Save edit"
        >
          <CheckIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};
